import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getDispatcherStats, tickDispatcher, type DispatcherStats } from "@/lib/api";
import { Button, Card, ErrorBanner, PageContainer, toErrorMessage } from "@/components/Forms";
import { RefreshIcon } from "@/components/Icons";

export default function DispatcherPage() {
  const { t } = useTranslation();
  const [stats, setStats] = useState<DispatcherStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [reloadAt, setReloadAt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    getDispatcherStats()
      .then((s) => !cancelled && setStats(s))
      .catch((e) => !cancelled && setError(toErrorMessage(e, t("dispatcher.loadError"))));
    return () => {
      cancelled = true;
    };
  }, [reloadAt, t]);

  async function tick(tenant: string) {
    setBusy(tenant);
    setInfo(null);
    setError(null);
    try {
      const res = await tickDispatcher(tenant);
      setInfo(`${tenant}: processed ${res.processed} leads (${res.durationMs} ms)`);
      setReloadAt((n) => n + 1);
    } catch (e) {
      setError(toErrorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <PageContainer
      title={t("dispatcher.title")}
      subtitle={t("dispatcher.subtitle")}
      actions={
        <Button onClick={() => setReloadAt((n) => n + 1)}>
          <RefreshIcon size={14} /> {t("forms.refresh")}
        </Button>
      }
    >
      <ErrorBanner message={error} />
      {info && (
        <div className="mb-4 rounded-md border border-accent/30 bg-accent/10 px-3 py-2 text-sm">
          {info}
        </div>
      )}

      <Card
        title={t("dispatcher.title")}
        description={
          stats?.workerId
            ? `worker_id=${stats.workerId}`
            : undefined
        }
      >
        {stats === null ? (
          <p className="text-sm text-muted-foreground">{t("forms.loading")}</p>
        ) : stats.tenants.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("dispatcher.empty")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-2 py-2">{t("dispatcher.colTenant")}</th>
                  <th className="text-right px-2 py-2">{t("dispatcher.colDue")}</th>
                  <th className="text-right px-2 py-2">{t("dispatcher.colStuck")}</th>
                  <th className="text-right px-2 py-2">Retry-high</th>
                  <th className="text-left px-2 py-2 hidden md:table-cell">{t("dispatcher.colLastTick")}</th>
                  <th className="text-right px-2 py-2 hidden md:table-cell">Tick ms</th>
                  <th className="text-right px-2 py-2">{t("dispatcher.colAction")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {stats.tenants.map((tt) => (
                  <tr key={tt.tenant} className="hover:bg-muted/30">
                    <td className="px-2 py-2 font-mono">{tt.tenant}</td>
                    <td className="px-2 py-2 text-right font-mono">{tt.dueCount ?? 0}</td>
                    <td className={`px-2 py-2 text-right font-mono ${(tt.stuckClaim ?? 0) > 0 ? "text-amber-400" : ""}`}>
                      {tt.stuckClaim ?? 0}
                    </td>
                    <td className="px-2 py-2 text-right font-mono">{tt.retryHigh ?? 0}</td>
                    <td className="px-2 py-2 hidden md:table-cell text-xs text-muted-foreground">
                      {tt.lastTickAt ?? "—"}
                    </td>
                    <td className="px-2 py-2 hidden md:table-cell text-right font-mono text-xs">
                      {tt.lastTickDurationMs ?? "—"}
                    </td>
                    <td className="px-2 py-2 text-right">
                      <Button onClick={() => tick(tt.tenant)} disabled={busy !== null}>
                        {busy === tt.tenant ? t("dispatcher.ticking") : t("dispatcher.tick")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </PageContainer>
  );
}
